import React, { createContext, useContext, useCallback, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { BASE_URL, BASE_URL_API, AXIOS_OPTIONS } from "./Constants";
import axios from 'axios';
import text from './lib/dictionary.json';
import Alert from './components/reusable/Alert';
import AppDialog from './components/reusable/AppDialog';
import Review from './components/modular/Review';
import { pageFromType } from './RedirectToLogin';
import Spinner from './components/reusable/Spinner';
import UserSettings from './components/UserSettings';
import { post } from './services/http';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
    const [theme, setTheme] = useState('light-theme');
    const [lang, setLang] = useState('en');
    const [email, setEmail] = useState(localStorage.getItem('moei_crm-email') || '');
    const [user, setUser] = useState({});
    const [authed, setAuthed] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [token, setToken] = useState("");
    const [model, setModel] = useState('meetings');
    const [openSettings, setOpenSettings] = useState(false);

    const [auto, setAuto] = useState({
        meetings: false,
        bookings: false,
        transportation: false,
        loading: true
    });
    const [alertState, setAlertState] = useState({
        open: false,
        message: '',
        type: 'info'
    });
    const [confirmState, setConfirmState] = useState({
        open: false,
        message: '',
        type: 'info',
        onConfirm: null,
        onCancel: null
    });
    const [reviewState, setReviewState] = useState({
        open: false,
        user_id: 0,
        row_id: 0,
        row_type: '',
        columns: []
    });
    const [currentFormState, setCurrentFormState] = useState({ id: 0, rowType: '' });
    const navigate = useNavigate();
    const location = useLocation();
    const prevSettingsRef = useRef(null);

    // axios.defaults.headers.common['Authorization'] = "Bearer " + token;
    axios.defaults.withCredentials = true;

    const checkAuthStatus = useCallback(async () => {
        const publicPages = ['/login', '/forgot-password', '/reset-password','/contact-us'];
        if (publicPages.includes(location.pathname)) {
            setAuthLoading(false);
            return;
        }

        setAuthLoading(true);
        try {
            const response = await axios.get(`${BASE_URL_API}/user/me`);
            if (response.status === 200 && response.data.authed) {
                const { user, token, authed } = response.data;
                const { email } = user;
                const theme = user?.settings?.others?.theme;
                const lang = user?.settings?.others?.lang;

                setUser(user);
                setEmail(email);
                setTheme(theme ?? 'light-theme');
                setLang(lang ?? 'en');
                setToken(token);
                setAuthed(authed);
                setModel(location.pathname.split('/')[1]);
                prevSettingsRef.current = JSON.stringify(user.settings ?? {});
            } else {
                setAlertState({ open: true, type: 'error', message: "Auth status check returned not authed." });
                cleanUpAuthStates();
                navigate("/login");
            }
        } catch (error) {
            location.pathname !== '/' && setAlertState({
                open: true,
                type: 'error',
                message: "Authentication check failed:" + (error.response?.data?.message || error.message)
            });
            cleanUpAuthStates();
            navigate("/login");
        } finally {
            setAuthLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        checkAuthStatus();
    }, [checkAuthStatus]);

    useEffect(() => {
        if (!user?.id) return;

        const settingsStr = JSON.stringify(user.settings ?? {});

        if (prevSettingsRef.current === null) {
            prevSettingsRef.current = settingsStr;
            return;
        }

        if (prevSettingsRef.current === settingsStr) return;
        prevSettingsRef.current = settingsStr;

        const delayDebounceFn = setTimeout(() => {
            axios.post(
                `${BASE_URL_API}/user/update/${user.id}`,
                { settings: user.settings },
                AXIOS_OPTIONS
            ).catch(err => {
                console.error('Auto-save failed:', err);
            });
        }, 1000);

        return () => clearTimeout(delayDebounceFn);

    }, [user?.id, user?.settings]);

    useEffect(() => {
        if (authed) {
            setAuto(prev => ({ ...prev, loading: true }));
            axios.post(
                `${BASE_URL_API}/option/list`,
                JSON.stringify(['meetings-auto', 'bookings-auto', 'transportation-auto']),
                AXIOS_OPTIONS
            ).then(res => {
                const newAuto = {};
                res.data.forEach(({ name, value }) => {
                    const key = name.split('-')[0];
                    newAuto[key] = value;
                });
                setAuto(newAuto);
            }).catch(
                err => setAlertState({ open: true, type: 'error', message: err.response?.data?.message ?? err.message })
            ).finally(() => setAuto(prev => ({ ...prev, loading: false })));
        }
    }, [authed]);

    const cleanUpAuthStates = () => {
        setEmail('');
        setTheme('light-theme');
        setLang('en');
        setToken('');
        setAuthed(false);
        setUser({});
        prevSettingsRef.current = null;
        localStorage.removeItem('moei_crm-email');
    };

    const createReview = (body) => {
        const { open, ...rest } = reviewState;
        axios.post(
            `${BASE_URL_API}/review/create`,
            JSON.stringify({ ...rest, ...body }),
            AXIOS_OPTIONS
        ).then(() => {
            body.status !== 'Canceled' && setAlertState({
                open: true,
                message: text[lang]['review-success'],
                type: 'success'
            });
        }).catch(err => setAlertState({ open: true, message: err.response?.data?.message ?? err.message, type: 'error' }));
    };

    const reviewCancel = () => {
        // createReview({ status: 'Canceled' });
    };

    const reviewSubmit = (rating, comment) => {
        createReview({ rating, comment, status: 'Active' });
    };

    const loginAction = async (email, password) => {
        try {
            const response = await axios.post(BASE_URL + "/login/auth",
                JSON.stringify({ email, password }),
                AXIOS_OPTIONS
            );
            if ('success' === response.data.message) {
                const { user, token } = response.data;
                const { type } = user;
                const theme = user?.settings?.others?.theme;
                const lang = user?.settings?.others?.lang;

                // axios.defaults.headers.common['Authorization'] = "Bearer " + token;
                axios.defaults.withCredentials = true;

                setUser(user);
                setEmail(email);
                setAuthed(true);
                setToken(token);
                setTheme(theme ?? 'light-theme');
                setLang(lang ?? 'en');
                localStorage.setItem('moei_crm-email', email);
                prevSettingsRef.current = JSON.stringify(user.settings ?? {});
                navigate(pageFromType(type));
            } else {
                setAlertState({ open: true, type: 'error', message: "Wrong email or password" });
            }
        } catch (err) {
            setAlertState({ open: true, message: err.response?.data?.message ?? err.message, type: 'error' });
        }
    };

    const logOut = async () => {
        try {
            await post('/user/update/' + user.id, { settings: user.settings });
        } catch (error) {
            console.error("Failed to save settings before logout:", error);
        } finally {
            try {
                await post('/user/logout');
            } catch (err) {
                console.error("Logout failed at server level:", err);
            } finally {
                cleanUpAuthStates();
                navigate("/login");
            }
        }
    };

    const switchOrganization = async (organizationId) => {
        try {
            const response = await axios.post(BASE_URL + '/login/auth/switch-organization', {
                organization_id: organizationId
            });


            const { user, token } = response.data;

            // axios.defaults.headers.common['Authorization'] = "Bearer " + token;
            axios.defaults.withCredentials = true;

            setUser(user);
            setAuthed(true);
            setToken(token);
            // localStorage.setItem('moei_crm-email', email);
            // navigate(pageFromType(type));
            // 4. (Optional) Force a refresh or redirect to dashboard 
            // to clear any old tenant-specific data in other states
            window.location.reload();

        } catch (error) {
            throw error;
        }
    };
    return (
        <AuthContext.Provider value={{
            theme, setTheme,
            lang, setLang,
            openSettings, setOpenSettings,
            user, setUser,
            model, setModel,
            auto, setAuto,
            alertState, setAlertState,
            confirmState, setConfirmState,
            reviewState, setReviewState,
            currentFormState, setCurrentFormState,
            email,
            token,
            authed,
            loading: authLoading,
            loginAction,
            logOut,
            switchOrganization
        }}>
            <Alert
                theme={theme}
                lang={lang}
                type={alertState.type || ''}
                position={lang === 'en' ? 'left' : 'right'}
                open={alertState.open}
                message={alertState.message || ''}
                close={() => setAlertState(prev => ({ ...prev, open: false }))} />
            <AppDialog
                theme={theme}
                lang={lang}
                type='confirm'
                variant={confirmState.type}
                message={confirmState.message || ''}
                open={confirmState.open}
                showCancel={typeof confirmState.onCancel === 'function'}
                onClose={() => setConfirmState(prev => ({ ...prev, open: false }))}
                onCancel={confirmState.onCancel}
                onConfirm={confirmState.onConfirm}
                confirmText={text[lang]['ok']}
                cancelText={text[lang]['cancel']} />

            <div
                className={`App ${theme} ${lang}`}
                dir={lang === 'en' ? 'ltr' : 'rtl'}
                style={{
                    // flexDirection: lang === 'en' ? '' : 'row-reverse',
                    '--app-direction': lang === 'en' ? 'to bottom left' : 'to bottom right',
                }}>
                {reviewState.open && <Review
                    open={reviewState.open}
                    theme={theme}
                    lang={lang}
                    setAlertState={setAlertState}
                    id={reviewState.row_id}
                    type={reviewState.row_type}
                    columns={reviewState.columns}
                    onSubmit={reviewSubmit}
                    onCancel={reviewCancel}
                    onClose={() => setReviewState(prev => ({ ...prev, open: false }))} />}
                <UserSettings />
                {children
                }
            </div>
        </AuthContext.Provider>
    );
};

export default AuthProvider;

export const useAuth = () => {
    return useContext(AuthContext);
};